import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLocation } from "wouter";

export default function PrivacyPolicy() {
  const [, navigate] = useLocation();

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      {/* Header Navigation */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container flex items-center justify-between py-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-accent hover:text-accent-foreground transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-medium">חזרה לעמוד הבית</span>
          </button>
          <h1 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
            מדיניות פרטיות
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Last Updated */}
          <div className="text-sm text-muted-foreground border-r-4 border-accent pr-4">
            עדכון אחרון: מרץ 2026
          </div>

          {/* Introduction */}
          <section className="space-y-4">
            <p className="text-lg leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              הפרטיות שלך חשובה לנו. מסמך זה מפרט איזה מידע נאסף באתר וכיצד אנו משתמשים בו כדי להעניק לך את השירות הטוב ביותר.
            </p>
          </section>

          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
              1. המידע שאנו אוספים
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              במהלך השימוש באתר, עשוי להיאסף מידע מהסוגים הבאים:
            </p>
            <ul className="space-y-3 mr-6">
              <li className="flex gap-3">
                <span className="text-accent font-bold">•</span>
                <span style={{ fontFamily: "var(--font-body)" }}>
                  <strong>מידע שניתן בהסכמה:</strong> שם, מספר טלפון או כתובת דוא"ל, במידה ובחרת להשאיר פרטים בטופס יצירת קשר או לקבוע תור.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent font-bold">•</span>
                <span style={{ fontFamily: "var(--font-body)" }}>
                  <strong>מידע טכני:</strong> כתובת IP, סוג דפדפן ונתוני גלישה סטטיסטיים (באמצעות Cookies) כדי להבין איך המשתמשים פועלים באתר ולשפר את ביצועיו. האתר עושה שימוש בשירותי צד ג' (כגון Google Analytics) לצורך ניתוח סטטיסטי של תנועת הגולשים.
                </span>
              </li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
              2. השימוש במידע
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              המידע משמש אותנו למטרות הבאות בלבד:
            </p>
            <ul className="space-y-3 mr-6">
              <li className="flex gap-3">
                <span className="text-accent font-bold">•</span>
                <span style={{ fontFamily: "var(--font-body)" }}>מתן מענה לפניות וניהול תורים/שירותים.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent font-bold">•</span>
                <span style={{ fontFamily: "var(--font-body)" }}>שיפור חווית המשתמש ותחזוקת האתר.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent font-bold">•</span>
                <span style={{ fontFamily: "var(--font-body)" }}>שליחת עדכונים או מידע שיווקי (רק במידה וניתנה הסכמה מפורשת לכך). השארת פרטים בטפסים מהווה הסכמה לקבלת תוכן תפעולי ושיווקי, כאשר ניתן להסיר את עצמך מרשימת התפוצה בכל עת בלחיצה על 'הסר' או פנייה אלינו.</span>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
              3. העברת מידע לצדדים שלישיים
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              אנו מחויבים לשמירה על פרטיותך. המידע שלך לא יימכר או יועבר לצדדים שלישיים למטרות מסחריות. העברת מידע תתבצע רק במקרים של ספקי שירותים טכניים (כמו אחסון האתר) או במידה ונידרש לכך על פי חוק. מידע עשוי להישמר בשרתי ענן מאובטחים של ספקי תשתיות טכנולוגיות (כגון Meta, Google, Vercel) לצורך תפעול שוטף של שירותי האתר.
            </p>
          </section>

          {/* Section 4 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
              4. אבטחת מידע
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              אנו נוקטים באמצעי זהירות מקובלים כדי להגן על המידע האישי שלך. עם זאת, יש לזכור כי העברת מידע ברשת האינטרנט אינה בטוחה ב-100%, ולכן איננו יכולים להבטיח חסינות מוחלטת מפני גישה בלתי מורשית.
            </p>
          </section>

          {/* Section 5 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
              5. הזכויות שלך
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              זכותך לבקש לעיין במידע שנשמר עליך, לעדכן אותו או לבקש את מחיקתו מהמאגרים שלנו בכל עת באמצעות פנייה ישירה.
            </p>
          </section>

          {/* Section 6 - Cookies */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
              6. עוגיות (Cookies)
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              האתר משתמש בעוגיות לצורך שיפור חווית הגלישה וניתוח נתונים סטטיסטיים. עוגיות הן קבצים קטנים המאוחסנים במחשבך ומאפשרים לנו להבין את התנהגות המשתמשים. ניתן להשבית עוגיות דרך הגדרות הדפדפן שלך, אך זה עלול להשפיע על חלק מהתכונות באתר.
            </p>
          </section>

          {/* Section 7 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: "var(--font-header)" }}>
              7. יצירת קשר
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              בכל שאלה או בקשה בנוגע לפרטיות שלך באתר, ניתן ליצור קשר עם שרון דרך פרטי ההתקשרות המופיעים באתר.
            </p>
          </section>

          {/* Back Button */}
          <div className="pt-8 border-t border-border">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-body)" }}
            >
              <span>חזרה לעמוד הבית</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16 py-8 bg-background/50">
        <div className="container text-center text-sm text-muted-foreground">
          <p style={{ fontFamily: "var(--font-body)" }}>© 2026 שרון גולדברג - מאמנת כושר ובריאות. כל הזכויות שמורות.</p>
        </div>
      </footer>
    </div>
  );
}
